import { ChartBar } from '@phosphor-icons/react';
import {
  useFfeStats,
  type WdlEntry,
  type FfeWdlByColor,
  type FfeStatsResponse,
} from '@/api/ratings';

/**
 * WdlByColorMatrix — matrice W/D/L par couleur ET cadence (Stats Hub V2 PR #H).
 *
 * Reproduit le screenshot 2 du user : 4 sections (Total + Standard + Rapide +
 * Blitz), chacune avec 2 barres horizontales empilées (WHITE + BLACK)
 * décomposant Win / Draw / Loss + total.
 *
 * Couleurs sémantiques (DESIGN.md §2) :
 *  - Win  : `--volt-500` (succès, "tu as trouvé")
 *  - Draw : `--ink-300` (neutre)
 *  - Loss : `--coral-500` (regression / blunder)
 *
 * Données : `useFfeStats()` → endpoint `/users/me/ffe/stats` (PR #E).
 * Empty state si toutes les cadences ont `total === 0` : tagline FFE attendue.
 *
 * Pas de Recharts : barres en divs flex avec `data-testid` pour les tests.
 * Cohérent avec `EloHistoryChart` (SVG natif, pas de lib externe).
 */

type SectionKey = 'total' | 'standard' | 'rapide' | 'blitz';

interface SectionConfig {
  key: SectionKey;
  label: string;
}

const SECTIONS: ReadonlyArray<SectionConfig> = [
  { key: 'total', label: 'Total Games' },
  { key: 'standard', label: 'Standard Games' },
  { key: 'rapide', label: 'Rapide Games' },
  { key: 'blitz', label: 'Blitz Games' },
];

export interface WdlByColorMatrixProps {
  /** Optionnel : filtre `since=YYYY-MM-DD` (par défaut tout l'historique). */
  since?: string;
}

export function WdlByColorMatrix({ since }: WdlByColorMatrixProps): JSX.Element {
  const q = useFfeStats(since);

  return (
    <div
      className="bg-ink-800 border border-ink-700 rounded-xl p-5"
      data-testid="wdl-by-color-matrix"
    >
      <header className="flex items-center gap-2 mb-3">
        <ChartBar size={18} weight="regular" color="var(--paper-50)" aria-hidden="true" />
        <h3 className="text-heading-3 text-paper-50 m-0">Win / Draw / Loss par couleur</h3>
      </header>
      <p className="text-body-sm text-ink-300 italic mb-4">
        Source FFE — toutes parties officielles, croisées couleur × cadence.
      </p>

      {q.isLoading && (
        <p className="text-ink-300 font-mono text-body-sm py-6 text-center">Chargement…</p>
      )}

      {q.isError && !q.isLoading && (
        <p
          role="alert"
          className="text-coral-500 font-mono text-body-sm py-4"
          data-testid="wdl-error"
        >
          Impossible de charger les stats FFE.
        </p>
      )}

      {q.data && <MatrixBody data={q.data} />}
    </div>
  );
}

function MatrixBody({ data }: { data: FfeStatsResponse }): JSX.Element {
  const grandTotal = data.total.all.total;
  if (grandTotal === 0) {
    return (
      <p
        className="text-ink-300 italic text-body-sm py-6 text-center"
        data-testid="wdl-empty"
      >
        Aucune partie FFE enregistrée pour le moment. Le scraper alimentera ce
        tableau après le prochain tournoi.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="wdl-sections">
      {SECTIONS.map((section) => (
        <WdlSection key={section.key} label={section.label} data={data[section.key]} />
      ))}
    </div>
  );
}

function WdlSection({
  label,
  data,
}: {
  label: string;
  data: FfeWdlByColor;
}): JSX.Element {
  return (
    <section data-testid={`wdl-section-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h4 className="text-paper-50 font-display font-semibold text-body uppercase tracking-wider m-0">
          {label}
        </h4>
        <span
          className="text-ink-300 font-mono text-body-sm tabular-nums"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {data.all.total} parties
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <WdlBar colorLabel="Blanc" bucket={data.white} />
        <WdlBar colorLabel="Noir" bucket={data.black} />
      </div>
    </section>
  );
}

function WdlBar({
  colorLabel,
  bucket,
}: {
  colorLabel: 'Blanc' | 'Noir';
  bucket: WdlEntry;
}): JSX.Element {
  const safeTotal = Math.max(1, bucket.total);
  const winPct = (bucket.wins / safeTotal) * 100;
  const drawPct = (bucket.draws / safeTotal) * 100;
  const lossPct = (bucket.losses / safeTotal) * 100;
  const isEmpty = bucket.total === 0;

  return (
    <div
      className="grid grid-cols-[3rem_1fr_auto] items-center gap-3"
      data-testid={`wdl-bar-${colorLabel.toLowerCase()}`}
    >
      <span className="text-ink-300 font-mono text-body-sm uppercase tracking-wider">
        {colorLabel}
      </span>
      <div
        className="h-6 rounded-md bg-ink-900 border border-ink-700 overflow-hidden flex"
        aria-label={`${colorLabel} : ${bucket.wins} victoires, ${bucket.draws} nuls, ${bucket.losses} défaites`}
      >
        {isEmpty ? (
          <span className="m-auto text-ink-300 font-mono text-[11px] uppercase tracking-wider">
            Aucune partie
          </span>
        ) : (
          <>
            <div
              className="h-full"
              style={{ width: `${winPct}%`, backgroundColor: 'var(--volt-500)' }}
              data-testid={`bar-win-${colorLabel.toLowerCase()}`}
              title={`${bucket.wins} victoires`}
            />
            <div
              className="h-full"
              style={{ width: `${drawPct}%`, backgroundColor: 'var(--ink-300)' }}
              data-testid={`bar-draw-${colorLabel.toLowerCase()}`}
              title={`${bucket.draws} nuls`}
            />
            <div
              className="h-full"
              style={{ width: `${lossPct}%`, backgroundColor: 'var(--coral-500)' }}
              data-testid={`bar-loss-${colorLabel.toLowerCase()}`}
              title={`${bucket.losses} défaites`}
            />
          </>
        )}
      </div>
      <span
        className="text-paper-50 font-mono text-body-sm tabular-nums whitespace-nowrap"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {bucket.wins}V / {bucket.draws}N / {bucket.losses}D
      </span>
    </div>
  );
}
